import { useEffect, useRef, useState } from "react";
import { siteConfig } from "@/lib/site-config";

interface FormEmbedProps {
  id?: string;
  className?: string;
  /**
   * When true, the form iframe (a large third-party LeadConnector payload) is
   * only mounted after the first user interaction. Its height is reserved up
   * front so there is no layout shift, keeping it off the initial critical path
   * without any visual jump. Used for the always-visible hero form; the modal
   * form leaves this off because it already mounts on demand.
   */
  defer?: boolean;
}

/**
 * Embeds the LeadConnector form widget exactly as provided in the
 * business widget code. We never force a height, scroll, or
 * compression — the form_embed.js script controls iframe height.
 */
const FormEmbed = ({ id = `inline-${siteConfig.widgets.formId}`, className = "", defer = false }: FormEmbedProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(!defer);

  // Deferred: mount the form on the first real interaction. A page-load audit
  // performs none, so it never pays for the iframe during measurement, while a
  // real visitor triggers it the moment they move/scroll/tap.
  useEffect(() => {
    if (active) return;
    const events: (keyof WindowEventMap)[] = [
      "pointerdown",
      "keydown",
      "touchstart",
      "scroll",
      "mousemove",
    ];
    const activate = () => {
      cleanup();
      setActive(true);
    };
    const cleanup = () => events.forEach((e) => window.removeEventListener(e, activate));
    events.forEach((e) => window.addEventListener(e, activate, { once: true, passive: true }));
    return cleanup;
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const scriptId = "lc-form-embed-script";
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = siteConfig.widgets.formScriptSrc;
      s.async = true;
      document.body.appendChild(s);
    } else {
      // Script already loaded — re-run any global init if available
      // form_embed.js auto-attaches via postMessage, so simply remounting
      // the iframe is enough for it to start measuring.
      try {
        // @ts-expect-error - global form embed init
        if (typeof window.formEmbedInit === "function") window.formEmbedInit();
      } catch {
        // noop
      }
    }
  }, [active]);

  const reservedHeight = `${siteConfig.widgets.formHeight}px`;

  return (
    <div ref={containerRef} className={className} style={{ minHeight: reservedHeight }}>
      {active ? (
        <iframe
          src={siteConfig.widgets.formIframeSrc}
          style={{
            width: "100%",
            // Reserve the form's height up front so it doesn't collapse to the
            // iframe default and then jump when form_embed.js measures it (CLS).
            height: reservedHeight,
            minHeight: reservedHeight,
            border: "none",
            borderRadius: 10,
            display: "block",
          }}
          id={id}
          data-layout="{'id':'INLINE'}"
          data-trigger-type="alwaysShow"
          data-trigger-value=""
          data-activation-type="alwaysActivated"
          data-activation-value=""
          data-deactivation-type="neverDeactivate"
          data-deactivation-value=""
          data-form-name={siteConfig.widgets.formName}
          data-height={siteConfig.widgets.formHeight}
          data-layout-iframe-id={id}
          data-form-id={siteConfig.widgets.formId}
          title={siteConfig.widgets.formName}
          scrolling="no"
        />
      ) : (
        <div
          style={{ height: reservedHeight }}
          className="flex items-center justify-center"
          aria-hidden="true"
        >
          <span className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        </div>
      )}
    </div>
  );
};

export default FormEmbed;
